import { Link } from "react-router-dom"
import styled from "styled-components"
import Analytics from "icons/analytics.svg?react"
import Leads from "icons/leads.svg?react"
import CarPictures from "icons/carPictures.svg?react"
import LeadInsights from "icons/executiveSummary.svg?react"
import MarketScanner from "icons/marketScanner.svg?react"
import MarketValuation from "icons/marketValuation.svg?react"
import PricingRules from "icons/pricingRules.svg?react"
import Account from "icons/manageUser.svg?react"
import Packages from "icons/packages.svg?react"
import Invoices from "icons/invoices.svg?react"
import { Settings } from "@autouncle/icons"
import type { NavigationLinkProps } from "./NavigationLink"
import type ProductStatus from "DealerApp/types/ProductStatus"
import type { NavigationBarTexts } from "DealerApp/types/texts"
import NavIcon from "./NavIcon"

export const RegularLink = styled.a`
  width: inherit;
  text-decoration: none;
`

const RouterLink = styled(Link)`
  width: inherit;
  text-decoration: none;
`

const SettingsIcon = NavIcon(Settings)

interface NavigationLink {
  category: string
  items: Omit<NavigationLinkProps, "currentPathname" | "sidebarCollapsed">[]
}
interface NavigationLinksProps {
  dealerToolLinks: Record<string, string>
  texts: NavigationBarTexts
  productStatuses: Record<string, ProductStatus>
}

function activeLink(productStatus: ProductStatus): boolean {
  // In case product has landing page, make page available all the time
  const pageActive = productStatus.active
  return pageActive && productStatus.userAccess
}

/** This function returns an array of dealer links */
export const navigationLinks = ({
  dealerToolLinks,
  texts,
  productStatuses,
}: NavigationLinksProps): NavigationLink[] => {
  return [
    {
      category: texts.categories.inventory,
      items: [
        {
          active: activeLink(productStatuses.analytics),
          label: texts.labels.analytics,
          Icon: Analytics,
          link: {
            to: dealerToolLinks.analytics,
            component: RegularLink,
          },
        },
        {
          active: activeLink(productStatuses.inventory),
          label: texts.labels.inventory,
          Icon: Analytics,
          link: dealerToolLinks.inventoryLink
            ? {
                to: dealerToolLinks.inventoryLink,
                component: RegularLink,
              }
            : {
                to: "/pro/inventory",
                component: RouterLink,
              },
        },
      ],
    },
    {
      category: texts.categories.websiteAndConversion,
      items: [
        {
          active: activeLink(productStatuses.leadBox),
          label: texts.labels.leadBox,
          Icon: Leads,
          link: {
            to: dealerToolLinks.leadbox,
            component: RegularLink,
          },
        },
        {
          active: activeLink(productStatuses.carPictures),
          label: texts.labels.customerPictures,
          Icon: CarPictures,
          link: {
            to: dealerToolLinks.carPictures,
            component: RegularLink,
          },
        },
        {
          active: activeLink(productStatuses.leadInsights),
          label: texts.labels.leadInsights,
          Icon: LeadInsights,
          link: dealerToolLinks.leadInsightsLink
            ? {
                to: dealerToolLinks.leadInsightsLink,
                additionalActiveRoutes: [
                  (path: string) => path.endsWith("/leadbox/executive_summary"),
                ],
                component: RegularLink,
              }
            : {
                to: "/pro/lead-insights",
                component: RouterLink,
              },
        },
      ],
    },
    {
      category: texts.categories.sourcingAndValuationTools,
      items: [
        {
          active: activeLink(productStatuses.marketScanner),
          label: texts.labels.marketScanner,
          Icon: MarketScanner,
          link: dealerToolLinks.marketScannerLink
            ? {
                to: dealerToolLinks.marketScannerLink,
                component: RegularLink,
              }
            : {
                to: "/pro/scanner",
                component: RouterLink,
              },
        },
        {
          active: activeLink(productStatuses.marketValuation),
          label: texts.labels.marketValuation,
          labelBadge:
            !productStatuses.marketValuation.active && texts.badges.free,
          Icon: MarketValuation,
          link: dealerToolLinks.marketValuationLink
            ? {
                to: dealerToolLinks.marketValuationLink,
                component: RegularLink,
              }
            : {
                to: "/pro/valuations", // link to Past Valuations
                additionalActiveRoutes: [
                  (path: string) => "/pro/valuations/new" === path, // link to New Valuation
                  (path: string) => {
                    // link to a specific Past Valuation
                    // "/valuations" segment may or may not be present
                    return /^(\/pro\/valuations)?\/[0-9]+/.test(path)
                  },
                ],
                component: RouterLink,
              },
        },
        {
          active: activeLink(productStatuses.pricingRules),
          label: texts.labels.pricingRules,
          Icon: PricingRules,
          link: {
            to: dealerToolLinks.pricingRules,
            component: RegularLink,
          },
        },
      ],
    },
    {
      category: texts.categories.accountAndBilling,
      items: [
        {
          active: activeLink(productStatuses.billing),
          label: texts.labels.billing,
          Icon: Invoices,
          link: dealerToolLinks.accountAndBillingLink
            ? {
                to: dealerToolLinks.accountAndBillingLink,
                component: RegularLink,
              }
            : {
                to: "/pro/invoices",
                additionalActiveRoutes: [
                  (path: string) => "/pro/invoices/payment_methods" === path,
                  (path: string) => {
                    return /\/pro\/invoices\/[0-9]+/.test(path)
                  },
                ],
                component: RouterLink,
              },
        },
        {
          active: activeLink(productStatuses.users),
          label: texts.labels.users,
          Icon: Account,
          link: dealerToolLinks.usersLink
            ? {
                to: dealerToolLinks.usersLink,
                component: RegularLink,
              }
            : {
                to: "/pro/users",
                component: RouterLink,
              },
        },
      ],
    },
    {
      category: "",
      items: [
        {
          active: activeLink(productStatuses.settings),
          label: "Settings",
          Icon: SettingsIcon,
          link: dealerToolLinks.settingsLink
            ? {
                to: dealerToolLinks.settingsLink,
                component: RegularLink,
              }
            : {
                to: "/pro/settings",
                component: RouterLink,
              },
        },
      ],
    },
  ]
}
